import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface Batch {
  id: string;
  sku: string;
  marketplace: string;
  batch_name: string;
  shipment_id: string;
  order_date: string;
  arrival_date: string;
  units_ordered: number;
  units_received: number;
  units_remaining: number;
  units_depleted: number;
  factory_unit_price: number;
  factory_currency: string;
  exchange_rate: number;
  freight_cost: number;
  freight_method: string;
  duties_cost: number;
  vat_cost: number;
  insurance_cost: number;
  inspection_cost: number;
  dispatch_cost: number;
  other_cost: number;
  other_cost_notes: string;
  total_landed_cost: number;
  landed_unit_cost: number;
  status: string;
  created_at: string;
}

interface CogsSummary {
  sku: string;
  marketplace: string;
  total_remaining: number;
  batch_count: number;
  fifo_cogs: number;
  weighted_avg_cogs: number;
}

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE', A1C3SOZF4HXIYN: 'PL', A1S75YUIWTBMNN: 'BE',
};
const MARKETPLACE_FLAGS: Record<string, string> = {
  ATVPDKIKX0DER: '\u{1F1FA}\u{1F1F8}', A2EUQ1WTGCTBG2: '\u{1F1E8}\u{1F1E6}', A1AM78C64UM0Y8: '\u{1F1F2}\u{1F1FD}',
  A1F83G8C2ARO7P: '\u{1F1EC}\u{1F1E7}', A1PA6795UKMFR9: '\u{1F1E9}\u{1F1EA}', A13V1IB3VIYZZH: '\u{1F1EB}\u{1F1F7}',
  APJ6JRA9NG5V4: '\u{1F1EE}\u{1F1F9}', A1RKKUPIHCS9HS: '\u{1F1EA}\u{1F1F8}', A1805IZSGTT6HS: '\u{1F1F3}\u{1F1F1}',
  A2NODRKZP88ZB9: '\u{1F1F8}\u{1F1EA}', A1C3SOZF4HXIYN: '\u{1F1F5}\u{1F1F1}', A1S75YUIWTBMNN: '\u{1F1E7}\u{1F1EA}',
};
const MARKETPLACE_OPTIONS = [
  { id: 'ATVPDKIKX0DER', label: 'US' }, { id: 'A2EUQ1WTGCTBG2', label: 'CA' }, { id: 'A1AM78C64UM0Y8', label: 'MX' },
  { id: 'A1F83G8C2ARO7P', label: 'UK' }, { id: 'A1PA6795UKMFR9', label: 'DE' }, { id: 'A13V1IB3VIYZZH', label: 'FR' },
  { id: 'APJ6JRA9NG5V4', label: 'IT' }, { id: 'A1RKKUPIHCS9HS', label: 'ES' },
];

const STATUSES = ['ordered', 'in_transit', 'received', 'depleted'] as const;
const STATUS_COLORS: Record<string, string> = {
  ordered: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-yellow-100 text-yellow-700',
  received: 'bg-green-100 text-green-700',
  depleted: 'bg-gray-100 text-gray-500',
};
const FREIGHT_METHODS = ['', 'sea', 'air', 'express', 'truck', 'mixed'];

// ── Component ────────────────────────────────────────────────────────────────

export function InventoryBatches() {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [summary, setSummary] = useState<CogsSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { displayName, parentNickname } = useNicknames();
  const [filterSku, setFilterSku] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMkt, setFilterMkt] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());

  // Detail view
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => { load(); }, [filterSku, filterStatus, filterMkt]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterSku) params.set('sku', filterSku);
      if (filterStatus) params.set('status', filterStatus);
      if (filterMkt) params.set('marketplace', filterMkt);
      const [batchData, cogsData] = await Promise.all([
        api.get<{ batches: Batch[] }>(`/inventory/batches?${params}`),
        api.get<{ summary: CogsSummary[] }>('/inventory/batches/cogs-summary'),
      ]);
      setBatches(batchData.batches);
      setSummary(cogsData.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  function emptyForm() {
    return {
      sku: '', marketplace: '', batch_name: '', order_date: '', arrival_date: '',
      units_ordered: '', units_received: '', factory_unit_price: '',
      factory_currency: 'USD', exchange_rate: '1',
      freight_cost: '0', freight_method: '', duties_cost: '0',
      vat_cost: '0', insurance_cost: '0', inspection_cost: '0',
      dispatch_cost: '0', other_cost: '0', other_cost_notes: '',
      status: 'ordered' as string,
    };
  }

  function openAdd() {
    setEditId(null);
    setForm(emptyForm());
    setShowForm(true);
  }

  function openEdit(b: Batch) {
    setEditId(b.id);
    setForm({
      sku: b.sku,
      marketplace: b.marketplace ?? '',
      batch_name: b.batch_name,
      order_date: b.order_date,
      arrival_date: b.arrival_date,
      units_ordered: String(b.units_ordered),
      units_received: String(b.units_received),
      factory_unit_price: String(b.factory_unit_price),
      factory_currency: b.factory_currency,
      exchange_rate: String(b.exchange_rate),
      freight_cost: String(b.freight_cost),
      freight_method: b.freight_method,
      duties_cost: String(b.duties_cost),
      vat_cost: String(b.vat_cost),
      insurance_cost: String(b.insurance_cost),
      inspection_cost: String(b.inspection_cost),
      dispatch_cost: String(b.dispatch_cost),
      other_cost: String(b.other_cost),
      other_cost_notes: b.other_cost_notes,
      status: b.status,
    });
    setShowForm(true);
  }

  async function handleSave() {
    setError('');
    setSuccess('');
    const payload = {
      sku: form.sku.trim(),
      marketplace: form.marketplace,
      batch_name: form.batch_name,
      order_date: form.order_date,
      arrival_date: form.arrival_date,
      units_ordered: Number(form.units_ordered),
      units_received: Number(form.units_received || 0),
      factory_unit_price: Number(form.factory_unit_price),
      factory_currency: form.factory_currency,
      exchange_rate: Number(form.exchange_rate || 1),
      freight_cost: Number(form.freight_cost || 0),
      freight_method: form.freight_method,
      duties_cost: Number(form.duties_cost || 0),
      vat_cost: Number(form.vat_cost || 0),
      insurance_cost: Number(form.insurance_cost || 0),
      inspection_cost: Number(form.inspection_cost || 0),
      dispatch_cost: Number(form.dispatch_cost || 0),
      other_cost: Number(form.other_cost || 0),
      other_cost_notes: form.other_cost_notes,
      status: form.status,
    };

    if (!payload.sku || !payload.units_ordered) {
      setError('SKU and Units Ordered are required');
      return;
    }

    try {
      if (editId) {
        await api.put(`/inventory/batches/${editId}`, payload);
        setSuccess('Batch updated');
      } else {
        const res = await api.post<{ id: string; landed_unit_cost: number }>('/inventory/batches', payload);
        setSuccess(`Batch created — landed cost: $${res.landed_unit_cost.toFixed(2)}/unit`);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this batch and its depletion history?')) return;
    try {
      await api.delete(`/inventory/batches/${id}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }

  // Live preview of landed cost
  const previewLanded = (() => {
    const qty = Number(form.units_ordered) || 0;
    if (qty <= 0) return null;
    const factoryTotal = qty * (Number(form.factory_unit_price) || 0) * (Number(form.exchange_rate) || 1);
    const extras = [form.freight_cost, form.duties_cost, form.vat_cost, form.insurance_cost,
      form.inspection_cost, form.dispatch_cost, form.other_cost]
      .reduce((s, v) => s + (Number(v) || 0), 0);
    const total = factoryTotal + extras;
    return { total, perUnit: total / qty };
  })();

  if (loading && batches.length === 0) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Purchase Batches (FIFO COGS)</h2>
          <p className="text-sm text-gray-500">Track batches with full landed cost. COGS auto-updates as batches deplete.</p>
        </div>
        <button onClick={openAdd} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
          Add Batch
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3"><p className="text-sm text-red-600">{error}</p></div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-3"><p className="text-sm text-green-600">{success}</p></div>}

      {/* COGS Summary Cards */}
      {summary.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="text-xs font-semibold text-gray-500 mb-3">FIFO COGS per SKU</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {summary.map((s) => (
              <div key={`${s.sku}-${s.marketplace}`} className="rounded-lg border border-gray-100 p-3">
                <div className="text-sm font-medium text-gray-900" title={s.sku}>
                  {parentNickname(s.sku) && <div className="text-[10px] text-gray-400 font-normal">{parentNickname(s.sku)}</div>}
                  <div className="truncate">
                    {s.marketplace && <span className="mr-1">{MARKETPLACE_FLAGS[s.marketplace] ?? ''}</span>}
                    {displayName(s.sku)}
                    {s.marketplace && <span className="ml-1 text-xs text-gray-400">{MARKETPLACE_LABELS[s.marketplace] ?? ''}</span>}
                  </div>
                </div>
                <div className="mt-1 flex items-baseline gap-3">
                  <span className="text-lg font-semibold text-brand-600">${s.fifo_cogs?.toFixed(2) ?? '—'}</span>
                  <span className="text-xs text-gray-400">FIFO</span>
                </div>
                <div className="mt-0.5 text-xs text-gray-500">
                  {s.total_remaining} units in {s.batch_count} batch{s.batch_count !== 1 ? 'es' : ''}
                  {s.weighted_avg_cogs != null && <span className="ml-2">Avg: ${s.weighted_avg_cogs.toFixed(2)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Needs Cost Alert */}
      {batches.some(b => b.factory_unit_price === 0 && b.status !== 'depleted') && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center justify-between">
          <p className="text-sm text-amber-700">
            {batches.filter(b => b.factory_unit_price === 0 && b.status !== 'depleted').length} batch(es) need cost data — auto-created from inbound shipments.
          </p>
          <button onClick={() => setFilterStatus('')}
            className="text-xs font-medium text-amber-600 hover:text-amber-800 underline">Show all</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={filterSku}
          onChange={(e) => setFilterSku(e.target.value)}
          placeholder="Filter by SKU..."
          className="w-48 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <select
          value={filterMkt}
          onChange={(e) => setFilterMkt(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Marketplaces</option>
          {MARKETPLACE_OPTIONS.map((m) => <option key={m.id} value={m.id}>{MARKETPLACE_FLAGS[m.id]} {m.label}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Batch Form */}
      {showForm && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-semibold mb-4">{editId ? 'Edit Batch' : 'New Batch'}</h3>

          {/* Row 1: Core */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-4">
            <Field label="SKU *" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} disabled={!!editId} />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Marketplace *</label>
              <select value={form.marketplace} onChange={(e) => setForm({ ...form, marketplace: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500">
                <option value="">— Select —</option>
                {MARKETPLACE_OPTIONS.map((m) => <option key={m.id} value={m.id}>{MARKETPLACE_FLAGS[m.id]} {m.label}</option>)}
              </select>
            </div>
            <Field label="Batch Name" value={form.batch_name} onChange={(v) => setForm({ ...form, batch_name: v })} placeholder="PO-2026-001" />
            <Field label="Order Date" value={form.order_date} onChange={(v) => setForm({ ...form, order_date: v })} type="date" />
            <Field label="Arrival Date" value={form.arrival_date} onChange={(v) => setForm({ ...form, arrival_date: v })} type="date" />
          </div>

          {/* Row 2: Quantities + Factory */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-4">
            <Field label="Units Ordered *" value={form.units_ordered} onChange={(v) => setForm({ ...form, units_ordered: v })} type="number" />
            <Field label="Units Received" value={form.units_received} onChange={(v) => setForm({ ...form, units_received: v })} type="number" />
            <Field label="Factory Price/Unit *" value={form.factory_unit_price} onChange={(v) => setForm({ ...form, factory_unit_price: v })} type="number" step="0.01" />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Currency</label>
              <select value={form.factory_currency} onChange={(e) => setForm({ ...form, factory_currency: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500">
                {['USD', 'EUR', 'GBP', 'CAD', 'CNY', 'MXN'].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Exchange Rate" value={form.exchange_rate} onChange={(v) => setForm({ ...form, exchange_rate: v })} type="number" step="0.0001" />
          </div>

          {/* Row 3: Cost Components */}
          <div className="border-t border-gray-100 pt-4 mb-4">
            <p className="text-xs font-medium text-gray-500 mb-3">Landed Cost Components (total for batch)</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Freight</label>
                <div className="flex gap-2">
                  <input type="number" step="0.01" value={form.freight_cost} onChange={(e) => setForm({ ...form, freight_cost: e.target.value })}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
                  <select value={form.freight_method} onChange={(e) => setForm({ ...form, freight_method: e.target.value })}
                    className="w-20 rounded-lg border border-gray-300 px-2 py-2 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500">
                    {FREIGHT_METHODS.map((m) => <option key={m} value={m}>{m || '—'}</option>)}
                  </select>
                </div>
              </div>
              <Field label="Duties" value={form.duties_cost} onChange={(v) => setForm({ ...form, duties_cost: v })} type="number" step="0.01" />
              <Field label="VAT (non-recoverable)" value={form.vat_cost} onChange={(v) => setForm({ ...form, vat_cost: v })} type="number" step="0.01" />
              <Field label="Insurance" value={form.insurance_cost} onChange={(v) => setForm({ ...form, insurance_cost: v })} type="number" step="0.01" />
              <Field label="Inspection" value={form.inspection_cost} onChange={(v) => setForm({ ...form, inspection_cost: v })} type="number" step="0.01" />
              <Field label="Dispatch Fee" value={form.dispatch_cost} onChange={(v) => setForm({ ...form, dispatch_cost: v })} type="number" step="0.01" />
              <Field label="Other Cost" value={form.other_cost} onChange={(v) => setForm({ ...form, other_cost: v })} type="number" step="0.01" />
              <Field label="Other Notes" value={form.other_cost_notes} onChange={(v) => setForm({ ...form, other_cost_notes: v })} />
            </div>
          </div>

          {/* Row 4: Status + Preview */}
          <div className="flex items-end justify-between border-t border-gray-100 pt-4">
            <div className="flex items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              {previewLanded && (
                <div className="rounded-lg bg-brand-50 border border-brand-200 px-4 py-2">
                  <span className="text-xs text-brand-600">Landed Cost:</span>
                  <span className="ml-2 text-sm font-semibold text-brand-700">${previewLanded.perUnit.toFixed(2)}/unit</span>
                  <span className="ml-2 text-xs text-brand-500">(${previewLanded.total.toFixed(2)} total)</span>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">{editId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Table */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-sm font-semibold mb-4">Batches ({batches.length})</h3>
        {batches.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No batches yet. Add your first purchase batch above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">SKU</th>
                  <th className="pb-2 text-center text-xs font-medium text-gray-500">Mkt</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Batch</th>
                  <th className="pb-2 text-center text-xs font-medium text-gray-500">Status</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Ordered</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Remaining</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Factory/u</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Landed/u</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Freight</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Ship</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Dates</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {batches.map((b) => (
                  <tr key={b.id} className={expandedId === b.id ? 'bg-gray-50' : ''}>
                    <td className="py-2 font-medium text-gray-900 max-w-[160px]" title={b.sku}>
                      {parentNickname(b.sku) && <span className="text-[10px] text-gray-400 block font-normal">{parentNickname(b.sku)}</span>}
                      {displayName(b.sku)}
                      {b.factory_unit_price === 0 && b.status !== 'depleted' && (
                        <span className="ml-1 inline-block rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700">needs cost</span>
                      )}
                    </td>
                    <td className="py-2 text-center text-sm" title={b.marketplace}>
                      {MARKETPLACE_FLAGS[b.marketplace] ?? '—'}
                      <div className="text-[10px] text-gray-400">{MARKETPLACE_LABELS[b.marketplace] ?? ''}</div>
                    </td>
                    <td className="py-2 text-gray-600 max-w-[120px] truncate" title={b.batch_name}>
                      {b.batch_name || '—'}
                      {b.shipment_id && <div className="text-[10px] text-gray-400 truncate" title={b.shipment_id}>FBA: {b.shipment_id}</div>}
                    </td>
                    <td className="py-2 text-center">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[b.status] ?? 'bg-gray-100 text-gray-500'}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="py-2 text-right text-gray-700">{b.units_ordered}</td>
                    <td className={`py-2 text-right font-medium ${b.units_remaining === 0 ? 'text-gray-400' : b.units_remaining <= 10 ? 'text-orange-600' : 'text-gray-900'}`}>
                      {b.units_remaining}
                    </td>
                    <td className="py-2 text-right font-mono text-gray-600">{b.factory_currency} {b.factory_unit_price.toFixed(2)}</td>
                    <td className="py-2 text-right font-mono font-semibold text-brand-700">${b.landed_unit_cost.toFixed(2)}</td>
                    <td className="py-2 text-right font-mono text-gray-500">${b.freight_cost.toFixed(0)}</td>
                    <td className="py-2 text-gray-500 text-xs">{b.freight_method || '—'}</td>
                    <td className="py-2 text-gray-500 text-xs">
                      {b.order_date && <div>Ord: {b.order_date}</div>}
                      {b.arrival_date && <div>Arr: {b.arrival_date}</div>}
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setExpandedId(expandedId === b.id ? null : b.id)}
                          className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50">
                          {expandedId === b.id ? 'Less' : 'More'}
                        </button>
                        <button onClick={() => openEdit(b)}
                          className="rounded border border-brand-200 px-2 py-0.5 text-xs text-brand-600 hover:bg-brand-50">Edit</button>
                        <button onClick={() => handleDelete(b.id)}
                          className="rounded border border-red-200 px-2 py-0.5 text-xs text-red-600 hover:bg-red-50">Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Expanded Detail */}
      {expandedId && (() => {
        const b = batches.find((x) => x.id === expandedId);
        if (!b) return null;
        const extras = b.duties_cost + b.vat_cost + b.insurance_cost + b.inspection_cost + b.dispatch_cost + b.other_cost;
        return (
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold mb-3">Cost Breakdown — {b.sku} / {b.batch_name || b.id.slice(0, 8)}</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
              <CostLine label="Factory" value={b.units_ordered * b.factory_unit_price * b.exchange_rate} />
              <CostLine label="Freight" value={b.freight_cost} sub={b.freight_method} />
              <CostLine label="Duties" value={b.duties_cost} />
              <CostLine label="VAT" value={b.vat_cost} />
              <CostLine label="Insurance" value={b.insurance_cost} />
              <CostLine label="Inspection" value={b.inspection_cost} />
              <CostLine label="Dispatch Fee" value={b.dispatch_cost} />
              <CostLine label="Other" value={b.other_cost} sub={b.other_cost_notes} />
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-6">
              <div><span className="text-xs text-gray-500">Total Landed:</span> <span className="font-semibold">${b.total_landed_cost.toFixed(2)}</span></div>
              <div><span className="text-xs text-gray-500">Per Unit:</span> <span className="font-semibold text-brand-700">${b.landed_unit_cost.toFixed(2)}</span></div>
              <div><span className="text-xs text-gray-500">Extras:</span> <span className="font-mono">${extras.toFixed(2)}</span></div>
              <div><span className="text-xs text-gray-500">Depleted:</span> <span className="font-mono">{b.units_depleted} units</span></div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Field({ label, value, onChange, type, step, placeholder, disabled }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; step?: string; placeholder?: string; disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input type={type ?? 'text'} step={step} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-100" />
    </div>
  );
}

function CostLine({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="flex items-baseline justify-between rounded-lg bg-gray-50 px-3 py-2">
      <span className="text-gray-600">{label}{sub ? <span className="ml-1 text-xs text-gray-400">({sub})</span> : null}</span>
      <span className={`font-mono ${value > 0 ? 'text-gray-900' : 'text-gray-400'}`}>${value.toFixed(2)}</span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-48 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-72 rounded bg-gray-200" />
        </div>
      </div>
      <div className="h-32 rounded-xl bg-gray-200" />
      <div className="h-64 rounded-xl bg-gray-200" />
    </div>
  );
}
